import { IGetDetectionPeriodSummary } from '../../../detections/queries';
import {
  Document,
  Page,
  Path,
  Text as PDFText,
  StyleSheet,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import moment from 'moment';

// Update the SecurityOverviewPDFProps interface to match what we're actually using
interface SecurityOverviewPDFProps {
  data: IGetDetectionPeriodSummary;
  start: number;
  end: number;
  highRiskData: HighRiskData | null;
  alertCategories: AlertCategory[];
  alertCategoriesTotal: number;
  mitreData: MitreTactic[];
}

export function SecurityOverviewPDF({
  data,
  start,
  end,
  highRiskData,
  alertCategories,
  alertCategoriesTotal,
  mitreData,
}: SecurityOverviewPDFProps) {
  const currentDate = moment().format('MMM D YYYY h:mm A');
  const startDate = moment(start * 1000).format('MMM D YYYY');
  const endDate = moment(end * 1000).format('MMM D YYYY');

  // Calculate date range string here
  const dateRangeString = (() => {
    if (!start || !end) return '';
    const days = Math.floor((end - start) / 86400);
    return `(${days} ${days === 1 ? 'day' : 'days'})`;
  })();

  return (
    <Document>
      {/* First page */}
      <Page size="A4" orientation="landscape" style={pdfDocumentStyles.page}>
        {/* Wrap header and first section in a container */}
        <View style={pdfDocumentStyles.topSection}>
          <View style={pdfDocumentStyles.header}>
            <InvestigatorLogo />
            <View style={pdfDocumentStyles.headerText}>
              <PDFText style={pdfDocumentStyles.heading}>
                Security Overview
              </PDFText>
              <PDFText style={pdfDocumentStyles.dateText}>
                Time: {startDate} - {endDate} {dateRangeString}
              </PDFText>
            </View>
            <PDFText style={pdfDocumentStyles.createdAt}>
              Created: {currentDate}
            </PDFText>
          </View>

          <PDFText style={pdfDocumentStyles.sectionSubheading}>
            Detection Trend
          </PDFText>
          <PDFDetectionTrend data={data} />
        </View>

        {/* Rest of the content */}
        <PDFText style={pdfDocumentStyles.sectionSubheading}>
          Highest Risk Detections
        </PDFText>
        <View style={pdfDocumentStyles.riskTablesContainer}>
          <View style={pdfDocumentStyles.riskTable}>
            <PDFText style={pdfDocumentStyles.riskTableHeading}>
              By Entity
            </PDFText>
            {highRiskData && (
              <PDFHighRiskEntitiesPanel
                data={highRiskData.getTopDetectionEntitiesPaginated}
              />
            )}
          </View>
          <View style={pdfDocumentStyles.riskTable}>
            <PDFText style={pdfDocumentStyles.riskTableHeading}>
              By Category
            </PDFText>
            <PDFAlertCategoriesTable
              data={alertCategories}
              totalItems={alertCategoriesTotal}
            />
          </View>
        </View>
      </Page>

      {/* Second page with MITRE ATT&CK Map */}
      <Page size="A4" orientation="landscape" style={pdfDocumentStyles.page}>
        <View style={pdfDocumentStyles.header}>
          <PDFText style={pdfDocumentStyles.heading}>MITRE ATT&CK Map</PDFText>
        </View>
        <PDFMitreAttackMap data={mitreData} />
      </Page>
    </Document>
  );
}

// Keep types at the top of the file
interface DetectionSummary {
  detection_count: number;
  total_detections: number;
  entity_id: string;
  entity_name: string;
  entity_category: string;
  entity_type: string;
  severity: number;
  tenant: string;
  tenant_entity: string;
  tenant_info: {
    tenant_id: string;
    tenant_display_name: string;
    __typename: string;
  };
  first_seen: number;
  last_seen: number;
  is_custom_severity: boolean;
  __typename: string;
}

interface PaginatedDetectionSummaries {
  offset: number;
  size: number;
  summaries: DetectionSummary[];
  total_items: number;
  __typename: string;
}

interface HighRiskData {
  getTopDetectionEntitiesPaginated: PaginatedDetectionSummaries;
}

// Add a new interface for alert categories
export interface AlertCategory {
  severity: number;
  name: string;
  detectionCount: number;
}

// Define the valid MITRE color keys
type MitreColorKey =
  | 'reconnaissance'
  | 'initialAccess'
  | 'execution'
  | 'defenseEvasion'
  | 'credentialAccess'
  | 'lateralMovement'
  | 'commandAndControl'
  | 'exfiltration'
  | 'impact'
  | 'discovery';

// Update the MITRE_COLORS type
const MITRE_COLORS: Record<MitreColorKey, string> = {
  reconnaissance: '#EDF2F7',
  initialAccess: '#FED7D7',
  execution: '#FED7E2',
  defenseEvasion: '#FAF5FF',
  credentialAccess: '#E9D8FD',
  lateralMovement: '#FEEBC8',
  commandAndControl: '#BEE3F8',
  exfiltration: '#C6F6D5',
  impact: '#FEB2B2',
  discovery: '#E2E8F0',
};

// Update the interfaces to match the transformed data
interface MitreTechnique {
  name: string; // Changed from mitre_attack_technique
  count: number; // Changed from entity_count
}

interface MitreTactic {
  name: string; // Changed from mitre_attack_tactic
  techniques: MitreTechnique[];
}

const styles = StyleSheet.create({
  table: {
    width: '100%',
    marginBottom: 10,
    borderRadius: 4,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 6,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    minHeight: 24,
  },
  tableHeader: {
    backgroundColor: '#2C5282',
    color: '#ffffff',
    fontWeight: 'bold',
    padding: 10,
  },
  tableHeaderText: {
    color: '#ffffff',
    fontSize: 9,
    fontWeight: 'bold',
  },
  label: {
    flex: 2,
    color: '#4A5568',
    paddingRight: 8,
    fontSize: 9,
  },
  value: {
    flex: 1,
    color: '#2D3748',
    fontWeight: 'bold',
    textAlign: 'right',
    fontSize: 9,
  },
  positiveChange: {
    color: '#38A169',
  },
  negativeChange: {
    color: '#E53E3E',
  },
  tableCell: {
    flex: 1,
    padding: 5,
    fontSize: 9,
  },
  numberCell: {
    flex: 0.5,
    padding: 5,
    textAlign: 'right',
    fontSize: 9,
  },
  scoreCell: {
    width: '60px',
    padding: 4,
    textAlign: 'center',
    fontSize: 9,
  },
  entityCell: {
    flex: 1,
    padding: 4,
    fontSize: 9,
  },
  detectionsCell: {
    width: '80px',
    padding: 4,
    textAlign: 'center',
    fontSize: 9,
  },
  detectionTrendContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
    marginTop: 2,
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  trendBox: {
    flex: 1,
    margin: 1,
    padding: 2,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    maxWidth: 380,
  },
  trendHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  trendHeaderContent: {
    flex: 1,
    marginLeft: 8,
    height: 32,
    justifyContent: 'space-between',
  },
  iconContainer: {
    width: 32,
    height: 32,
    marginRight: 8,
    borderRadius: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  entitiesIcon: {
    backgroundColor: '#E53E3E',
  },
  categoriesIcon: {
    backgroundColor: '#805AD5',
  },
  trendBoxTitle: {
    fontSize: 10,
    color: '#4A5568',
    marginBottom: 2,
  },
  trendNumber: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    marginVertical: 6,
    width: '100%',
  },
  trendChangeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  arrow: {
    marginRight: 5,
  },
  trendUp: {
    color: '#38A169',
  },
  trendDown: {
    color: '#E53E3E',
  },
  trendChangeText: {
    fontSize: 12,
  },
  mitreContainer: {
    marginTop: 4,
    padding: 0,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
  },
  mitreLegend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 8,
    gap: 10,
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    marginBottom: 8,
  },
  legendColor: {
    width: 16,
    height: 16,
    marginRight: 8,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 10,
    color: '#4A5568',
  },
  mitreTacticsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    padding: 8,
  },
  mitreTactic: {
    flex: 1,
    gap: 4,
    borderRightWidth: 1,
    borderRightColor: '#E2E8F0',
    paddingRight: 8,
  },
  mitreTacticLast: {
    borderRightWidth: 0,
    paddingRight: 0,
  },
  mitreTechnique: {
    padding: 4,
    marginBottom: 2,
    borderRadius: 4,
  },
  techniqueName: {
    fontSize: 8,
    color: '#2D3748',
    marginBottom: 4,
  },
  techniqueCount: {
    fontSize: 8,
    color: '#4A5568',
    textAlign: 'right',
  },
  tableFooter: {
    fontSize: 8,
    color: '#718096',
    padding: 4,
    textAlign: 'left',
  },
});

export const pdfDocumentStyles = StyleSheet.create({
  page: {
    padding: 12,
    paddingTop: 8,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerText: {
    flex: 1,
    alignItems: 'center',
    textAlign: 'center',
    marginLeft: -100,
  },
  createdAt: {
    fontSize: 8,
    color: '#718096',
    textAlign: 'right',
    width: 150,
  },
  heading: {
    fontSize: 18,
    marginBottom: 4,
    color: '#2D3748',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  dateText: {
    fontSize: 12,
    color: '#718096',
    marginBottom: 8,
    textAlign: 'center',
  },
  sectionSubheading: {
    fontSize: 12,
    marginBottom: 0,
    marginTop: 2,
    marginLeft: 4,
    padding: 0,
    color: '#4A5568',
    fontWeight: 'bold',
  },
  topSection: {
    marginBottom: 4,
  },
  riskTablesContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  riskTable: {
    flex: 1,
    margin: 4,
  },
  riskTableHeading: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#2D3748',
  },
});

interface PDFComponentProps {
  data: IGetDetectionPeriodSummary;
}

export function PDFEntitiesWithDetectionsPanel({ data }: PDFComponentProps) {
  const summary = data.getDetectionPeriodSummary;
  const changeValue = (summary.total_entities.pct_chg * 100).toFixed(1);
  const isPositiveChange = summary.total_entities.pct_chg > 0;

  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text
          style={styles.tableHeaderText}
          data-testid="entities-with-detections-header"
        >
          Entities with Detections
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="entities-current-period-label">
          Current Period:
        </Text>
        <Text style={styles.value} data-testid="entities-current-period-value">
          {summary.total_entities.current.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="entities-previous-period-label">
          Previous Period:
        </Text>
        <Text style={styles.value} data-testid="entities-previous-period-value">
          {summary.total_entities.previous.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="entities-change-label">
          Change:
        </Text>
        <Text
          style={[
            styles.value,
            isPositiveChange ? styles.positiveChange : styles.negativeChange,
          ]}
        >
          {isPositiveChange ? '+' : ''}
          {changeValue}%
        </Text>
      </View>
    </View>
  );
}

export function PDFTotalDetectionsPanel({ data }: PDFComponentProps) {
  const summary = data.getDetectionPeriodSummary;
  const changeValue = (summary.count.pct_chg * 100).toFixed(1);
  const isPositiveChange = summary.count.pct_chg > 0;

  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text
          style={styles.tableHeaderText}
          data-testid="detection-statistics-header"
        >
          Detection Statistics
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="detection-current-period-label">
          Current Period:
        </Text>
        <Text style={styles.value} data-testid="detection-current-period-value">
          {summary.count.current.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text
          style={styles.label}
          data-testid="detection-previous-period-label"
        >
          Previous Period:
        </Text>
        <Text
          style={styles.value}
          data-testid="detection-previous-period-value"
        >
          {summary.count.previous.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="detection-change-label">
          Change:
        </Text>
        <Text
          style={[
            styles.value,
            isPositiveChange ? styles.positiveChange : styles.negativeChange,
          ]}
        >
          {isPositiveChange ? '+' : ''}
          {changeValue}%
        </Text>
      </View>
    </View>
  );
}

export function PDFAlertCategoriesPanel({ data }: PDFComponentProps) {
  const summary = data.getDetectionPeriodSummary;
  const changeValue = (summary.total_alert_categories.pct_chg * 100).toFixed(1);
  const isPositiveChange = summary.total_alert_categories.pct_chg > 0;

  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text
          style={styles.tableHeaderText}
          data-testid="alert-categories-header"
        >
          Alert Categories
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="alert-categories-current-label">
          Current Period:
        </Text>
        <Text style={styles.value} data-testid="alert-categories-current-value">
          {summary.total_alert_categories.current.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text
          style={styles.label}
          data-testid="alert-categories-previous-label"
        >
          Previous Period:
        </Text>
        <Text
          style={styles.value}
          data-testid="alert-categories-previous-value"
        >
          {summary.total_alert_categories.previous.toLocaleString()}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.label} data-testid="alert-categories-change-label">
          Change:
        </Text>
        <Text
          style={[
            styles.value,
            isPositiveChange ? styles.positiveChange : styles.negativeChange,
          ]}
          data-testid="alert-categories-change-value"
        >
          {isPositiveChange ? '+' : ''}
          {changeValue}%
        </Text>
      </View>
    </View>
  );
}

interface PDFHighRiskTableProps {
  data: {
    getHighRiskEntities: {
      name: string;
      riskScore: number;
      detectionCount: number;
    }[];
  };
}

export function PDFHighRiskEntitiesTable({ data }: PDFHighRiskTableProps) {
  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text style={styles.tableCell} data-testid="high-risk-entity-header">
          Entity
        </Text>
        <Text style={styles.numberCell} data-testid="high-risk-score-header">
          Risk Score
        </Text>
        <Text
          style={styles.numberCell}
          data-testid="high-risk-detections-header"
        >
          Detections
        </Text>
      </View>
      {data.getHighRiskEntities?.map((entity, index: number) => (
        <View key={index} style={styles.tableRow}>
          <Text style={styles.tableCell} data-testid="high-risk-entity-name">
            {entity.name}
          </Text>
          <Text style={styles.numberCell} data-testid="high-risk-entity-score">
            {entity.riskScore.toFixed(2)}
          </Text>
          <Text
            style={styles.numberCell}
            data-testid="high-risk-entity-detections"
          >
            {entity.detectionCount}
          </Text>
        </View>
      ))}
    </View>
  );
}

export function PDFHighRiskEntitiesPanel({
  data,
}: {
  data: PaginatedDetectionSummaries;
}) {
  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text
          style={[styles.tableHeaderText, styles.scoreCell]}
          data-testid="high-risk-entities-header-score"
        >
          Score
        </Text>
        <Text
          style={[styles.tableHeaderText, styles.entityCell]}
          data-testid="high-risk-entities-header-entity"
        >
          Entity
        </Text>
        <Text
          style={[styles.tableHeaderText, styles.detectionsCell]}
          data-testid="high-risk-entities-header-detections"
        >
          Detections
        </Text>
      </View>
      {data.summaries
        .slice(0, 10)
        .map((entity: DetectionSummary, index: number) => (
          <View key={index} style={styles.tableRow}>
            <Text
              style={styles.scoreCell}
              data-testid={`high-risk-entities-row-score-${index}`}
            >
              {entity.severity}
            </Text>
            <Text
              style={styles.entityCell}
              data-testid={`high-risk-entities-row-entity-${index}`}
            >
              {entity.entity_name}
            </Text>
            <Text
              style={styles.detectionsCell}
              data-testid={`high-risk-entities-row-detections-${index}`}
            >
              {entity.detection_count}
            </Text>
          </View>
        ))}
      <Text style={styles.tableFooter} data-testid="high-risk-entities-footer">
        Showing {data.summaries.length} out of {data.total_items} rows
      </Text>
    </View>
  );
}

const truncateText = (text: string, maxLength: number) => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

export function PDFAlertCategoriesTable({
  data,
  totalItems,
}: {
  data: AlertCategory[];
  totalItems: number;
}) {
  return (
    <View style={styles.table}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        <Text
          style={[styles.tableHeaderText, styles.scoreCell]}
          data-testid="alert-categories-header-score"
        >
          Score
        </Text>
        <Text
          style={[styles.tableHeaderText, styles.entityCell]}
          data-testid="alert-categories-header-category"
        >
          Category
        </Text>
        <Text
          style={[styles.tableHeaderText, styles.detectionsCell]}
          data-testid="alert-categories-header-detections"
        >
          Detections
        </Text>
      </View>
      {data.slice(0, 10).map((category, index) => (
        <View key={index} style={styles.tableRow}>
          <Text
            style={styles.scoreCell}
            data-testid={`alert-categories-row-score-${index}`}
          >
            {category.severity}
          </Text>
          <Text
            style={styles.entityCell}
            data-testid={`alert-categories-row-category-${index}`}
          >
            {truncateText(category.name, 50)}
          </Text>
          <Text
            style={styles.detectionsCell}
            data-testid={`alert-categories-row-detections-${index}`}
          >
            {category.detectionCount}
          </Text>
        </View>
      ))}
      <Text style={styles.tableFooter} data-testid="alert-categories-footer">
        Showing 10 out of {totalItems} rows
      </Text>
    </View>
  );
}

// Add the icons as components
const EntitiesIcon = () => (
  <Svg viewBox="0 0 24 24" width={16} height={16}>
    <Path
      fill="#FFFFFF"
      d="m19.07 4.93-1.41 1.41A8.014 8.014 0 0 1 20 12a8 8 0 0 1-16 0c0-4.08 3.05-7.44 7-7.93v2.02a5.998 5.998 0 1 0 5.243 10.153A6 6 0 0 0 18 12c0-1.66-.67-3.16-1.76-4.24l-1.41 1.41A4 4 0 1 1 8 12c0-1.86 1.28-3.41 3-3.86v2.14c-.6.35-1 .98-1 1.72a2 2 0 0 0 4 0c0-.74-.4-1.38-1-1.72V2h-1a10 10 0 1 0 7.07 2.93Z"
    />
  </Svg>
);

const CategoriesIcon = () => (
  <Svg viewBox="0 0 24 24" width={16} height={16}>
    <Path
      fill="#FFFFFF"
      d="m19.07 4.93-1.41 1.41A8.014 8.014 0 0 1 20 12a8 8 0 0 1-16 0c0-4.08 3.05-7.44 7-7.93v2.02a5.998 5.998 0 1 0 5.243 10.153A6 6 0 0 0 18 12c0-1.66-.67-3.16-1.76-4.24l-1.41 1.41A4 4 0 1 1 8 12c0-1.86 1.28-3.41 3-3.86v2.14c-.6.35-1 .98-1 1.72a2 2 0 0 0 4 0c0-.74-.4-1.38-1-1.72V2h-1a10 10 0 1 0 7.07 2.93Z"
    />
  </Svg>
);

function UpArrow() {
  return (
    <Svg viewBox="0 0 24 24" width={16} height={16} style={styles.arrow}>
      <Path
        fill="currentColor"
        d="M4 12l1.41 1.41L11 7.83V20h2V7.83l5.58 5.59L20 12l-8-8-8 8z"
      />
    </Svg>
  );
}

function DownArrow() {
  return (
    <Svg viewBox="0 0 24 24" width={16} height={16} style={styles.arrow}>
      <Path
        fill="currentColor"
        d="M20 12l-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8 8-8z"
      />
    </Svg>
  );
}

export function PDFDetectionTrend({ data }: PDFComponentProps) {
  const summary = data.getDetectionPeriodSummary;
  const entitiesChange = summary.total_entities.pct_chg * 100;
  const categoriesChange = summary.total_alert_categories.pct_chg * 100;

  return (
    <View style={styles.detectionTrendContainer}>
      <View style={styles.trendBox}>
        <View style={styles.trendHeader}>
          <View style={[styles.iconContainer, styles.entitiesIcon]}>
            <EntitiesIcon />
          </View>
          <View style={styles.trendHeaderContent}>
            <Text
              style={styles.trendBoxTitle}
              data-testid="trend-entities-title"
            >
              Entities with Detections
            </Text>
            <Text style={styles.trendNumber} data-testid="trend-entities-value">
              {summary.total_entities.current}
            </Text>
          </View>
        </View>
        <View style={styles.divider} />
        <View style={styles.trendChangeContainer}>
          {entitiesChange >= 0 ? <UpArrow /> : <DownArrow />}
          <Text
            style={[
              styles.trendChangeText,
              entitiesChange >= 0 ? styles.trendUp : styles.trendDown,
            ]}
            data-testid="trend-entities-change"
          >
            {Math.abs(entitiesChange).toFixed(2)}%{' '}
            {entitiesChange >= 0 ? 'up' : 'down'}
          </Text>
        </View>
      </View>

      <View style={styles.trendBox}>
        <View style={styles.trendHeader}>
          <View style={[styles.iconContainer, styles.categoriesIcon]}>
            <CategoriesIcon />
          </View>
          <View style={styles.trendHeaderContent}>
            <Text
              style={styles.trendBoxTitle}
              data-testid="trend-categories-title"
            >
              Alert Categories With Detections
            </Text>
            <Text
              style={styles.trendNumber}
              data-testid="trend-categories-value"
            >
              {summary.total_alert_categories.current}
            </Text>
          </View>
        </View>
        <View style={styles.divider} />
        <View style={styles.trendChangeContainer}>
          {categoriesChange >= 0 ? <UpArrow /> : <DownArrow />}
          <Text
            style={[
              styles.trendChangeText,
              categoriesChange >= 0 ? styles.trendUp : styles.trendDown,
            ]}
            data-testid="trend-categories-change"
          >
            {Math.abs(categoriesChange).toFixed(2)}%{' '}
            {categoriesChange >= 0 ? 'up' : 'down'}
          </Text>
        </View>
      </View>
    </View>
  );
}

const legendItems: Array<{ key: MitreColorKey; label: string }> = [
  { key: 'reconnaissance', label: 'Reconnaissance' },
  { key: 'initialAccess', label: 'Initial Access' },
  { key: 'execution', label: 'Execution' },
  { key: 'defenseEvasion', label: 'Defense Evasion' },
  { key: 'credentialAccess', label: 'Credential Access' },
  { key: 'lateralMovement', label: 'Lateral Movement' },
  { key: 'commandAndControl', label: 'Command and Control' },
  { key: 'exfiltration', label: 'Exfiltration' },
  { key: 'impact', label: 'Impact' },
  { key: 'discovery', label: 'Discovery' },
];

export function PDFMitreAttackMap({ data }: { data: MitreTactic[] }) {
  const normalizeKey = (name: string): MitreColorKey => {
    return name
      .split(/\s+/)
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join('') as MitreColorKey;
  };

  // Create a set of used tactics from the data
  const usedTactics = new Set(data.map((tactic) => normalizeKey(tactic.name)));

  // Filter legend items to only show those that exist in the data
  const filteredLegendItems = legendItems.filter((item) =>
    usedTactics.has(item.key),
  );

  return (
    <View style={styles.mitreContainer}>
      <View style={styles.mitreLegend}>
        {filteredLegendItems.map((item) => (
          <View key={item.key} style={styles.legendItem}>
            <View
              style={[
                styles.legendColor,
                { backgroundColor: MITRE_COLORS[item.key] },
              ]}
            />
            <Text
              style={styles.legendText}
              data-testid={`mitre-legend-label-${item.key}`}
            >
              {item.label}
            </Text>
          </View>
        ))}
      </View>

      <View style={styles.mitreTacticsContainer}>
        {data.map((tactic, index) => {
          const tacticKey = normalizeKey(tactic.name);
          const isLastTactic = index === data.length - 1;

          const tacticStyle = isLastTactic
            ? [styles.mitreTactic, styles.mitreTacticLast]
            : styles.mitreTactic;

          return (
            <View key={index} style={tacticStyle}>
              {tactic.techniques.map((technique, techIndex) => (
                <View
                  key={techIndex}
                  style={[
                    styles.mitreTechnique,
                    { backgroundColor: MITRE_COLORS[tacticKey] },
                  ]}
                >
                  <Text
                    style={styles.techniqueName}
                    data-testid={`mitre-technique-name-${index}-${techIndex}`}
                  >
                    {technique.name}
                  </Text>
                  <Text
                    style={styles.techniqueCount}
                    data-testid={`mitre-technique-count-${index}-${techIndex}`}
                  >
                    {technique.count}
                  </Text>
                </View>
              ))}
            </View>
          );
        })}
      </View>
    </View>
  );
}

export function InvestigatorLogo() {
  return (
    <Svg viewBox="0 0 244 24" width={200} height={20}>
      <Path
        fill="#2C5282"
        fillRule="evenodd"
        d="M12 2.62A9.38 9.38 0 1021.38 12H24A12 12 0 1112 0v2.62z"
      />
      <Path
        fill="currentColor"
        fill-rule="evenodd"
        d="M85.41 15.402c-.78.712-1.522 1.072-2.207 1.072-.802 0-1.194-.389-1.194-1.186V7.537c0-.858.359-1.242 1.166-1.242.675 0 1.407.394 2.234 1.203v7.904zm.97-10.788h-.056c-.589 0-.915.322-.915.908v.009c-.786-.682-1.638-1.025-2.534-1.025-1.817 0-2.777.982-2.777 2.84v8.107c0 1.805 1.013 2.838 2.777 2.838.814 0 1.657-.35 2.507-1.045v3.86c0 .791-.38 1.16-1.192 1.16h-.96c-.852 0-1.249-.36-1.249-1.132v-.11c0-.353-.149-.773-.858-.773h-.082c-.556 0-.86.294-.86.828v.108c0 1.867 1.006 2.813 2.994 2.813h.957c2.086 0 3.188-1.067 3.188-3.084V5.522c0-.594-.326-.908-.94-.908zM76.057 19.769c.617 0 .943-.323.943-.935V5.641c0-.613-.326-.936-.943-.936h-.055c-.615 0-.94.324-.94.936v13.193c0 .612.325.935.94.935h.055zm0-19.436h-.055c-.615 0-.968.35-.968.963v.653c0 .613.353.964.968.964h.055c.617 0 .97-.351.97-.964v-.653c0-.613-.353-.963-.97-.963zM70.6 19.77c.617 0 .942-.323.942-.935V.934c0-.611-.325-.934-.942-.934h-.053c-.617 0-.943.324-.943.934v17.9c0 .612.326.935.943.935h.053zM61.583 7.9c0-1.014.474-1.486 1.495-1.486h.302c1 0 1.468.47 1.468 1.485v3.282h-3.265V7.898zm4.124 5.125c.695 0 1.078-.382 1.078-1.072v-4c0-2.226-1.136-3.356-3.377-3.356h-.385c-2.241 0-3.378 1.13-3.378 3.357v8.568c0 2.228 1.137 3.357 3.379 3.357h.384c2.24 0 3.377-1.13 3.377-3.357v-.978c0-.586-.324-.91-.913-.91h-.054c-.59 0-.916.324-.916.91v1.033c0 .994-.475 1.458-1.494 1.458h-.33c-1.02 0-1.495-.464-1.495-1.458v-3.552h4.124zm-11.194-8.32h-.054c-.582 0-.913.323-.913.883v13.247c0 .612.315.935.913.935h.055c.616 0 .942-.323.942-.935V7.782a4.16 4.16 0 011.68-.967c.83-.236 1.003-.74 1.003-1.12v-.162c0-.775-.469-.937-.858-.937-.566 0-1.165.41-1.825 1.247v-.256c0-.57-.336-.883-.943-.883zm-5.495 11.871c0 .995-.467 1.458-1.468 1.458h-.522c-1 0-1.465-.463-1.465-1.458V7.898c0-1.014.466-1.485 1.465-1.485h.523c1 0 1.467.47 1.467 1.484v8.678zM47.577 4.596h-.574c-2.242 0-3.38 1.13-3.38 3.357v8.57c0 2.227 1.138 3.355 3.38 3.355h.574c2.224 0 3.351-1.127 3.351-3.357V7.952c0-2.225-1.127-3.356-3.351-3.356zm-7.406 5.871h.055c.617 0 .942-.323.942-.936V7.898c0-2.19-1.12-3.302-3.323-3.302h-.466C35.137 4.596 34 5.726 34 7.953v8.568c0 2.228 1.137 3.357 3.379 3.357h.466c2.223 0 3.35-1.13 3.35-3.357v-2.068c0-.584-.336-.906-.942-.906h-.055c-.589 0-.913.322-.913.906v2.122c0 .995-.467 1.46-1.468 1.46h-.41c-1.003 0-1.47-.465-1.47-1.46V7.898c0-1.013.467-1.485 1.47-1.485h.383c1.001 0 1.467.47 1.467 1.485v1.633c0 .613.316.936.914.936zm54.471-5.87c-.862 0-1.722.362-2.563 1.084V.935c0-.612-.326-.935-.941-.935h-.056c-.598 0-.914.324-.914.935v17.9c0 .61.316.934.914.934h.056c.615 0 .94-.323.94-.935V7.514c.879-.759 1.612-1.13 2.236-1.13.747 0 1.11.39 1.11 1.187v11.261c0 .613.325.937.943.937h.055c.598 0 .913-.324.913-.937V7.272c0-1.75-.93-2.676-2.693-2.676zm7 15.172h-.055c-.617 0-.942-.323-.942-.935V6.55h-.262c-.565 0-.887-.321-.887-.881v-.054c0-.552.315-.856.888-.856h.261V3.192c0-.612.326-.935.942-.935h.055c.616 0 .941.323.941.935v1.566h.921c.564 0 .86.295.86.856v.054c0 .569-.305.882-.859.882h-.922v12.285c0 .61-.325.934-.941.934z"
        clip-rule="evenodd"
      ></Path>
      <Path fill="#646A70" fillRule="evenodd" d="M115.364 0v24h-1V0h1z" />
      <Path
        fill="#2D3748"
        d="M125.364 17.365V6.657h1.245v10.708h-1.245zM139.425 17.365h-1.436l-5.837-8.994h-.059l.051.82c.02.303.035.628.044.974.015.342.022.691.022 1.048v6.152h-1.157V6.657h1.428l5.816 8.965h.051c-.01-.171-.022-.413-.037-.725l-.044-1.018a41.769 41.769 0 01-.014-1.018V6.657h1.172v10.708zM151.325 6.657l-3.845 10.708h-1.253l-3.845-10.708h1.304l2.505 7.053c.102.283.193.554.271.813a15.892 15.892 0 01.388 1.443 16.77 16.77 0 01.388-1.45c.083-.264.176-.54.278-.828l2.491-7.031h1.318zM160.259 17.365h-5.962V6.657h5.962v1.099h-4.717v3.464h4.446v1.084h-4.446v3.962h4.717v1.099zM170.175 14.516c0 .635-.159 1.177-.476 1.626-.313.444-.752.784-1.319 1.018-.566.234-1.233.352-1.999.352-.406 0-.789-.02-1.15-.06a7.804 7.804 0 01-.996-.168 4.249 4.249 0 01-.799-.27V15.82a9.78 9.78 0 001.341.424c.527.127 1.079.19 1.655.19.537 0 .991-.07 1.362-.212.371-.146.652-.354.843-.622.195-.274.293-.6.293-.982 0-.366-.081-.671-.242-.915-.161-.25-.43-.474-.806-.674-.371-.205-.879-.422-1.523-.652a8.157 8.157 0 01-1.201-.527 3.736 3.736 0 01-.872-.66 2.49 2.49 0 01-.535-.849 3.23 3.23 0 01-.175-1.106c0-.571.144-1.06.432-1.465.293-.41.695-.722 1.208-.937.518-.22 1.111-.33 1.78-.33.571 0 1.099.054 1.582.161.488.108.935.252 1.34.432l-.388 1.07a8.473 8.473 0 00-1.23-.403 5.477 5.477 0 00-1.333-.161c-.459 0-.847.068-1.165.205-.312.132-.552.32-.718.564a1.513 1.513 0 00-.249.871c0 .376.078.689.235.938.161.249.415.471.761.666.352.19.816.391 1.392.601a9.76 9.76 0 011.604.732c.439.254.774.567 1.003.938.23.366.345.832.345 1.399zM177.153 17.365h-1.252v-9.61h-3.369V6.658h7.976v1.099h-3.355v9.609zM183.626 17.365V6.657h1.245v10.708h-1.245zM193.893 11.755h3.743v5.193c-.561.19-1.14.331-1.736.424-.591.093-1.252.14-1.985.14-1.113 0-2.053-.223-2.819-.667a4.3 4.3 0 01-1.736-1.897c-.396-.825-.594-1.804-.594-2.937 0-1.108.218-2.075.652-2.9a4.64 4.64 0 011.897-1.92c.83-.458 1.826-.688 2.989-.688.595 0 1.159.056 1.691.169a7.306 7.306 0 011.495.461l-.476 1.084a8.81 8.81 0 00-1.311-.44 5.883 5.883 0 00-1.473-.183c-.874 0-1.623.181-2.248.542a3.608 3.608 0 00-1.428 1.531c-.328.66-.491 1.44-.491 2.344 0 .894.144 1.672.432 2.336a3.28 3.28 0 001.348 1.539c.61.366 1.396.549 2.358.549.483 0 .898-.027 1.245-.08.347-.06.662-.128.945-.206v-3.281h-2.498v-1.113zM208.445 17.365l-1.318-3.398h-4.292l-1.311 3.398h-1.26l4.189-10.752h1.121l4.16 10.752h-1.289zm-1.692-4.504l-1.252-3.377a39.302 39.302 0 00-.147-.432l-.197-.623a52.337 52.337 0 00-.169-.52c-.049.2-.102.4-.161.601-.054.195-.107.378-.161.55l-.139.424-1.275 3.377h3.501zM216.01 17.365h-1.253v-9.61h-3.369V6.658h7.976v1.099h-3.354v9.609zM231.77 11.996c0 .825-.105 1.577-.315 2.256a4.914 4.914 0 01-.938 1.743c-.41.484-.92.857-1.53 1.121-.611.264-1.319.396-2.124.396-.83 0-1.556-.132-2.176-.396a4.067 4.067 0 01-1.538-1.128 4.943 4.943 0 01-.915-1.75 7.962 7.962 0 01-.301-2.256c0-1.094.181-2.054.542-2.879a4.208 4.208 0 011.641-1.926c.737-.459 1.66-.688 2.769-.688 1.059 0 1.95.227 2.673.68.728.455 1.277 1.094 1.648 1.92.376.82.564 1.79.564 2.907zm-8.525 0c0 .909.129 1.695.388 2.359.258.664.654 1.176 1.186 1.538.537.361 1.218.542 2.044.542.83 0 1.508-.18 2.036-.542.532-.362.925-.874 1.179-1.538s.381-1.45.381-2.359c0-1.382-.291-2.46-.872-3.237-.576-.781-1.477-1.172-2.702-1.172-.83 0-1.514.178-2.051.535-.537.356-.938.864-1.201 1.523-.259.655-.388 1.438-.388 2.351zM238.551 6.657c.879 0 1.604.11 2.175.33.576.214 1.006.544 1.289.988.283.445.425 1.011.425 1.7 0 .566-.103 1.04-.308 1.42a2.659 2.659 0 01-.805.916 4.119 4.119 0 01-1.062.542l2.937 4.812h-1.451l-2.622-4.468h-2.226v4.468h-1.245V6.657h2.893zm-.074 1.077h-1.574v4.108h1.699c.874 0 1.516-.178 1.926-.534.415-.357.623-.881.623-1.575 0-.727-.22-1.243-.659-1.545-.435-.303-1.106-.454-2.015-.454z"
      />
    </Svg>
  );
}
